import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

const BudgetChart = ({ data }) => {
  const months = [
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
  ];
  const budgetUsage = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

  const barChartData = {
    labels: months,
    datasets: [
      {
        label: "การใช้งบประมาณ (%)",
        data: budgetUsage,
        backgroundColor: "rgba(25, 85, 153, 0.7)",
        borderRadius: 6,
        barPercentage: 0.6,
        categoryPercentage: 0.8,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "#195599",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 10,
        cornerRadius: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          color: "#e5e7eb",
          drawBorder: false,
        },
        ticks: {
          stepSize: 20,
          color: "#6b7280",
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#6b7280",
        },
      },
    },
  };

  const donutData = {
    labels: ["งบประมาณแผ่นดิน", "งบประมาณ บกศ.", "อื่นๆ"],
    datasets: [
      {
        data: [85, 10, 5],
        backgroundColor: ["#195599", "#ffc107", "#10b981"],
        borderWidth: 0,
        borderRadius: 10,
      },
    ],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "65%",
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          padding: 20,
          color: "#374151",
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        backgroundColor: "#195599",
        titleColor: "#fff",
        bodyColor: "#fff",
        callbacks: {
          label: (context) => {
            return `${context.label}: ${context.raw}%`;
          },
        },
      },
    },
  };

  return (
    <div className="space-y-8">
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-1 h-6 bg-primary-500 rounded-full mr-3"></span>
            การใช้งบประมาณรายเดือน (%)
          </h3>
          <div className="h-[250px]">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>

        {/* Donut Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <span className="w-1 h-6 bg-secondary-500 rounded-full mr-3"></span>
            แหล่งงบประมาณ
          </h3>
          <div className="h-[250px] flex items-center justify-center">
            <div className="w-[220px]">
              <Doughnut data={donutData} options={donutOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bars Section */}
      {data?.budgetByDepartment && (
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
            <span className="w-1 h-6 bg-primary-500 rounded-full mr-3"></span>
            งบประมาณแยกตามหน่วยงาน
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column */}
            <div className="space-y-4">
              {data.budgetByDepartment
                .slice(0, Math.ceil(data.budgetByDepartment.length / 2))
                .map((item, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-gray-700 flex items-center">
                        <span
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        ></span>
                        {item.department}
                      </span>
                      <span className="font-semibold text-primary-500">
                        {item.percentage}%
                      </span>
                    </div>
                    <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${item.percentage}%`,
                          background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}dd 100%)`,
                        }}
                      >
                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-white/30"></div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {data.budgetByDepartment
                .slice(Math.ceil(data.budgetByDepartment.length / 2))
                .map((item, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-gray-700 flex items-center">
                        <span
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: item.color }}
                        ></span>
                        {item.department}
                      </span>
                      <span className="font-semibold text-primary-500">
                        {item.percentage}%
                      </span>
                    </div>
                    <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${item.percentage}%`,
                          background: `linear-gradient(90deg, ${item.color} 0%, ${item.color}dd 100%)`,
                        }}
                      >
                        <div className="absolute right-0 top-0 bottom-0 w-1 bg-white/30"></div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* Summary */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600">งบประมาณรวมทั้งหมด</span>
              <span className="text-xl font-bold text-primary-500">
                อยู่ระหว่างประมวลผล
                {/* {new Intl.NumberFormat("th-TH", {
                  style: "currency",
                  currency: "THB",
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                }).format(data?.totalBudget || 0)}*/}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetChart;
